/**
 * HTML design document model.
 *
 * The design JSON is the source of truth for the editor. `serializer/toHtml`
 * turns it into the HTML template (with {{placeholders}}) that the backend
 * renders and signs at issuance.
 */
import { DEFAULT_FONT_FAMILY } from "./fonts";
import { DEFAULT_PRINT, DEFAULT_SIZE_ID, createSize } from "./sizes";

export const SCHEMA_VERSION = 1;

export const ELEMENT_TYPE = Object.freeze({
  TEXT: "text",
  IMAGE: "image",
  QR: "qr",
  SHAPE: "shape",
  GROUP: "group",
});

export const SHAPE_KIND = Object.freeze({
  RECT: "rect",
  ELLIPSE: "ellipse",
  LINE: "line",
  SVG: "svg",
});

export const IMAGE_ROLES = [
  "image",
  "logo",
  "signature",
  "photo",
  "footer_logo",
  "background",
];

export const BACKGROUND_FILL_TYPE = Object.freeze({
  SOLID: "solid",
  LINEAR: "linear",
  RADIAL: "radial",
  IMAGE: "image",
});

export const QR_VARIABLE_KEY = "qr-code";

/**
 * @typedef {{ mode: "rgb"|"cmyk", rgb: string, cmyk?: { c: number, m: number, y: number, k: number } }} PrintColor
 *
 * @typedef {Object} BaseElement
 * @property {string} id
 * @property {"text"|"image"|"qr"|"shape"|"group"} type
 * @property {string} name        Shown in the Layers panel.
 * @property {number} x
 * @property {number} y
 * @property {number} width
 * @property {number} height
 * @property {number} rotation    Degrees, clockwise, around the element centre.
 * @property {number} opacity     0–1
 * @property {boolean} visible
 * @property {boolean} locked
 * @property {string|null} parentId  Group id when the element belongs to a group.
 *
 * @typedef {BaseElement & {
 *   type: "text", content: string, fontFamily: string, fontSize: number,
 *   fontWeight: number, fontStyle: "normal"|"italic",
 *   textDecoration: "none"|"underline"|"line-through",
 *   textTransform: "none"|"uppercase"|"lowercase"|"capitalize",
 *   letterSpacing: number, lineHeight: number,
 *   align: "left"|"center"|"right"|"justify",
 *   verticalAlign: "top"|"middle"|"bottom",
 *   color: string, printColor?: PrintColor,
 *   autoFit: { mode: "none"|"shrink", minFontSize: number }
 * }} TextElement
 *
 * @typedef {BaseElement & {
 *   type: "image", src: string, assetId?: string|null, variableKey?: string|null,
 *   role: string, objectFit: "contain"|"cover"|"fill",
 *   crop?: { x: number, y: number, width: number, height: number }|null,
 *   borderRadius: number, flipX: boolean, flipY: boolean
 * }} ImageElement
 *
 * @typedef {BaseElement & {
 *   type: "qr", variableKey: string, fg: string, bg: string, dotStyle: string,
 *   fgPrint?: PrintColor, bgPrint?: PrintColor
 * }} QrElement
 *
 * @typedef {BaseElement & {
 *   type: "shape", shape: "rect"|"ellipse"|"line"|"svg",
 *   fill: string|null, fillPrint?: PrintColor, stroke: string|null, strokePrint?: PrintColor,
 *   strokeWidth: number, dash: number[]|null, cornerRadius: number,
 *   svgMarkup?: string, flipX: boolean, flipY: boolean
 * }} ShapeElement
 *
 * @typedef {BaseElement & { type: "group" }} GroupElement
 *
 * @typedef {TextElement|ImageElement|QrElement|ShapeElement|GroupElement} DesignElement
 *
 * @typedef {{
 *   type: "solid"|"linear"|"radial"|"image",
 *   color: string,
 *   stops: { offset: number, color: string }[],
 *   angle: number,
 *   imageUrl: string|null, imageFit: "cover"|"contain"|"fill", imageOpacity: number
 * }} BackgroundFill
 *
 * @typedef {Object} HtmlDesign
 * @property {string} id
 * @property {number} schemaVersion
 * @property {number} version
 * @property {string} name
 * @property {"draft"|"published"} status
 * @property {ReturnType<typeof createSize>} size
 * @property {{ bleedMm: number, safeMarginMm: number, colorProfile: string }} print
 * @property {{ fill: BackgroundFill, printColor?: PrintColor, locked: boolean }} background
 * @property {DesignElement[]} elements  Array order is z-order (last is on top).
 * @property {Object<string, Object>} variables  Same shape as onboarding variableConfig.
 * @property {"encoded"|"base45"|"claim169"} qrCodeType
 * @property {{ family: string, source: string, weights: number[], italic: boolean }[]} fonts
 * @property {{ id: string, url: string, name: string, role: string, mime: string }[]} assets
 * @property {Object} meta
 */

let idCounter = 0;

export function createId(prefix = "el") {
  idCounter = (idCounter + 1) % 1e6;
  const random = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}${idCounter.toString(36)}${random}`;
}

const nowIso = () => new Date().toISOString();

function baseElement(type, partial = {}) {
  return {
    id: partial.id || createId(type),
    type,
    name: partial.name || type,
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    rotation: 0,
    opacity: 1,
    visible: true,
    locked: false,
    parentId: null,
    shadow: null,
    ...partial,
  };
}

/**
 * A drop shadow. Text uses `text-shadow` (the glyphs) and everything else
 * `box-shadow` (the box), which is what each one visually means.
 * @returns {{x:number,y:number,blur:number,color:string,opacity:number}}
 */
/**
 * A text outline. Certificate titles often sit on dark or busy artwork, where
 * an outline is what keeps them readable.
 * @returns {{color:string,width:number}}
 */
export function createTextOutline(partial = {}) {
  return { color: "#ffffff", width: 2, ...partial };
}

export function createShadow(partial = {}) {
  return { x: 0, y: 4, blur: 12, color: "#101828", opacity: 0.35, ...partial };
}

/** @returns {TextElement} */
export function createTextElement(partial = {}) {
  const content = partial.content ?? "Text";
  return baseElement(ELEMENT_TYPE.TEXT, {
    name: String(content).startsWith("{{") ? "Variable text" : "Text",
    width: 360,
    height: 48,
    content,
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: 24,
    fontWeight: 400,
    fontStyle: "normal",
    textDecoration: "none",
    textTransform: "none",
    letterSpacing: 0,
    lineHeight: 1.25,
    align: "center",
    verticalAlign: "middle",
    color: "#1f2937",
    outline: null,
    autoFit: { mode: "none", minFontSize: 8 },
    ...partial,
  });
}

/** @returns {ImageElement} */
export function createImageElement(partial = {}) {
  return baseElement(ELEMENT_TYPE.IMAGE, {
    name: "Image",
    width: 160,
    height: 120,
    src: "",
    assetId: null,
    variableKey: null,
    role: "image",
    objectFit: "contain",
    crop: null,
    borderRadius: 0,
    flipX: false,
    flipY: false,
    ...partial,
  });
}

/** @returns {QrElement} */
export function createQrElement(partial = {}) {
  return baseElement(ELEMENT_TYPE.QR, {
    name: "QR code",
    width: 120,
    height: 120,
    variableKey: QR_VARIABLE_KEY,
    fg: "#111827",
    bg: "#ffffff",
    dotStyle: "square",
    ...partial,
  });
}

/** @returns {ShapeElement} */
export function createShapeElement(partial = {}) {
  const shape = partial.shape || SHAPE_KIND.RECT;
  const isLine = shape === SHAPE_KIND.LINE;
  return baseElement(ELEMENT_TYPE.SHAPE, {
    name:
      { rect: "Rectangle", ellipse: "Ellipse", line: "Line", svg: "Ornament" }[
        shape
      ] || "Shape",
    width: isLine ? 240 : 160,
    height: isLine ? 4 : 120,
    shape,
    fill: isLine ? null : "#e0e4fe",
    stroke: isLine ? "#1f2937" : null,
    strokeWidth: isLine ? 2 : 0,
    dash: null,
    fillGradient: null,
    cornerRadius: 0,
    svgMarkup: "",
    flipX: false,
    flipY: false,
    ...partial,
  });
}

/** @returns {GroupElement} */
export function createGroupElement(partial = {}) {
  return baseElement(ELEMENT_TYPE.GROUP, { name: "Group", ...partial });
}

/** @returns {BackgroundFill} */
export function createBackgroundFill(partial = {}) {
  return {
    type: BACKGROUND_FILL_TYPE.SOLID,
    color: "#ffffff",
    stops: [
      { offset: 0, color: "#ffffff" },
      { offset: 1, color: "#e0e4fe" },
    ],
    angle: 135,
    imageUrl: null,
    imageFit: "cover",
    imageOpacity: 1,
    ...partial,
  };
}

/**
 * @param {{ sizeId?: string, name?: string, source?: "ai"|"gallery"|"blank", elements?: DesignElement[], background?: Object }} [options]
 * @returns {HtmlDesign}
 */
export function createEmptyDesign(options = {}) {
  const timestamp = nowIso();
  return {
    id: options.id || createId("design"),
    schemaVersion: SCHEMA_VERSION,
    version: 0,
    name: options.name || "Untitled design",
    status: "draft",
    size: createSize(options.sizeId || DEFAULT_SIZE_ID),
    print: { ...DEFAULT_PRINT },
    background: {
      fill: createBackgroundFill(options.background?.fill),
      printColor: options.background?.printColor,
      locked: false,
    },
    elements: options.elements || [],
    variables: {},
    qrCodeType: "encoded",
    fonts: [],
    assets: [],
    meta: {
      source: options.source || "blank",
      createdAt: timestamp,
      updatedAt: timestamp,
      description: options.description || "",
      ...(options.meta || {}),
    },
  };
}

/** Deep clone that is safe for design documents (plain JSON). */
export const cloneDesign = (design) => JSON.parse(JSON.stringify(design));

/** Ids of an element and, for groups, every descendant. */
export function collectDescendantIds(elements, id) {
  const ids = [id];
  for (let index = 0; index < ids.length; index += 1) {
    elements.forEach((element) => {
      if (element.parentId === ids[index]) ids.push(element.id);
    });
  }
  return ids;
}
